import { chordTones } from '../chord/chord-tones.js'
import { chordOutline } from './chord-outline.js'
import { distinctNotes, guideToneLine, type ChordLine } from './guide-tones.js'
import type { VocalRange } from './types.js'

/** A chart section as the song shows it: its lines, numbered as a reader counts them */
export interface ChartSection {
  name: string
  lines: { lineNumber: number; chords: string[] }[]
}

export interface ChordPart {
  kind: 'guide-tones' | 'outline'
  section: string
  /** The chart lines the part's chords come from, for "line 2" or "lines 2–3" */
  lines: { first: number; last: number }
  /** Set when one long line was split into pieces: "piece 1 of 2" */
  piece?: { index: number; of: number }
  chords: string[]
  line: ChordLine
}

/**
 * A guide-tone part left out, so the UI can say why (Joni writes the copy, M5). `one-note`: its guide
 * tones merge to a single held note, which is no exercise (GH #2455, Bowie). The outline still covers it.
 */
export interface SkippedPart {
  section: string
  lines: { first: number; last: number }
  reason: 'one-note'
}

/** A part must sing at least this many distinct notes to be offered at all */
const minOfferedNotes = 2

/** A part sings at least this many distinct notes, counted after merging repeats (Bowie, M2 pre-review) */
const minNotes = 4
const maxChords = 12
const outlineChords = 2

interface Group {
  first: number
  last: number
  chords: string[]
  piece?: { index: number; of: number }
}

/** Split a list into `count` runs as equal as possible, longer runs first */
const evenly = <T>(items: T[], count: number) => {
  const size = Math.floor(items.length / count)
  const extra = items.length % count
  return Array.from({ length: count }, (_value, index) => {
    const start = index * size + Math.min(index, extra)
    return items.slice(start, start + size + (index < extra ? 1 : 0))
  })
}

/**
 * D1 (Bowie, Joni): a part per chart line. A line whose guide tones sing fewer than 4 distinct notes
 * merges with the next in the section until the part sings 4, stopping before it would pass 12 chords; short lines left at the end join the part before them if that
 * keeps it to 12. A line of more than 12 is split into equal pieces of at most 12.
 */
const groupLines = (lines: ChartSection['lines'], range: VocalRange): Group[] => {
  const groups: Group[] = []
  let pending: Group | undefined
  for (const { lineNumber, chords } of lines) {
    if (chords.length === 0) {
      continue
    }
    if (chords.length > maxChords) {
      if (pending) {
        groups.push(pending)
        pending = undefined
      }
      const pieces = evenly(chords, Math.ceil(chords.length / maxChords))
      pieces.forEach((piece, index) =>
        groups.push({
          first: lineNumber,
          last: lineNumber,
          chords: piece,
          piece: { index: index + 1, of: pieces.length },
        })
      )
      continue
    }
    /** Merging stops before a part passes 12 chords (GH #2455, Bowie) */
    if (pending && pending.chords.length + chords.length > maxChords) {
      groups.push(pending)
      pending = undefined
    }
    const group: Group = pending
      ? { first: pending.first, last: lineNumber, chords: [...pending.chords, ...chords] }
      : { first: lineNumber, last: lineNumber, chords }
    if (distinctNotes(guideToneLine(group.chords, range).tones) >= minNotes) {
      groups.push(group)
      pending = undefined
      continue
    }
    pending = group
  }
  const previous = groups[groups.length - 1]
  if (pending && previous && !previous.piece && previous.chords.length + pending.chords.length <= maxChords) {
    groups[groups.length - 1] = {
      first: previous.first,
      last: pending.last,
      chords: [...previous.chords, ...pending.chords],
    }
    return groups
  }
  return pending ? [...groups, pending] : groups
}

/** The outline's parts: 2 sung chords each, in order across the section's lines */
const groupOutline = (lines: ChartSection['lines']): Group[] => {
  const chords = lines.flatMap(({ lineNumber, chords: symbols }) => symbols.map(chord => ({ lineNumber, chord })))
  const groups: Group[] = []
  for (let start = 0; start < chords.length; start += outlineChords) {
    const run = chords.slice(start, start + outlineChords)
    groups.push({
      first: run[0].lineNumber,
      last: run[run.length - 1].lineNumber,
      chords: run.map(({ chord }) => chord),
    })
  }
  return groups
}

/**
 * A chart section's practice parts (Pitch Trainer Phase 2, plan M2 step 2.3): guide-tone parts by chart
 * line, and chord-outline parts of 2 chords. Only chords that are sung count: N.C. and unknown symbols
 * are left out.
 */
export const chartParts = (
  section: ChartSection,
  range: VocalRange
): { guideTones: ChordPart[]; outlines: ChordPart[]; skipped: SkippedPart[] } => {
  const sung = section.lines.map(line => ({ ...line, chords: line.chords.filter(chord => chordTones(chord)) }))
  const toPart =
    (kind: ChordPart['kind'], build: (chords: string[], r: VocalRange) => ChordLine) =>
    ({ first, last, chords, piece }: Group): ChordPart => ({
      kind,
      section: section.name,
      lines: { first, last },
      ...(piece ? { piece } : {}),
      chords,
      line: build(chords, range),
    })
  const guideTones = groupLines(sung, range).map(toPart('guide-tones', guideToneLine))
  const isOffered = (part: ChordPart) => distinctNotes(part.line.tones) >= minOfferedNotes
  return {
    guideTones: guideTones.filter(isOffered),
    outlines: groupOutline(sung).map(toPart('outline', chordOutline)),
    skipped: guideTones
      .filter(part => !isOffered(part))
      .map(({ section: name, lines }) => ({ section: name, lines, reason: 'one-note' as const })),
  }
}
